import { useStackPageAugustRevisionEnabled } from "@mirage/august-revision-hook";
import { DEFAULT_SECTION_ID } from "@mirage/service-stacks/service/utils";
import { activeStackLinkSectionsMapAtom } from "@mirage/stacks/atoms";
import { useAtomValue } from "jotai";
import { useState } from "react";
import { DroppableEmptySection } from "../../DragAndDrop/DroppableEmptySection";
import { Section } from "./Section";

import type { stacks } from "@dropbox/api-v2-client";
import { DraggableProvided } from "react-beautiful-dnd";

interface SectionAreaProps {
  section: stacks.Section;
  onEditItem: (item: stacks.StackItemShortcut) => void;
  // this override flag is used to display the dfb august revision styles
  // when we want to render the new styles but can't feature gate users
  // ex. public view of the stacks page using (dfi styles, and we wanted to display dfb styles)
  alwaysUseAugustRevisionDfBStyles?: boolean;
  // Optional props for draggable sections
  draggableProps?: DraggableProvided;
}

export const SectionArea: React.FC<SectionAreaProps> = ({
  section,
  onEditItem,
  alwaysUseAugustRevisionDfBStyles,
  draggableProps,
}) => {
  const augustRevision = Boolean(
    useStackPageAugustRevisionEnabled() || alwaysUseAugustRevisionDfBStyles,
  );
  const linkSectionMap = useAtomValue(activeStackLinkSectionsMapAtom);

  const sectionId = section.id ?? DEFAULT_SECTION_ID;
  const items = linkSectionMap ? (linkSectionMap.get(sectionId) ?? []) : [];

  /**
   * Keep track of the DroppableEmptySection that the user is currently hovering over.
   * The DND library seems to not always fire when a user has stopped hovering over
   * a DroppableEmptySection, so we are keeping track of this state in the parent
   * to ensure that only one DroppableEmptySection is highlighted at a time.
   */
  const [emptySectionIdHoveringOver, setEmptySectionIdHoveringOver] = useState<
    string | null
  >(null);
  return (
    <div ref={draggableProps?.innerRef} {...draggableProps?.draggableProps}>
      <Section
        section={section}
        items={items}
        onEditItem={onEditItem}
        augustRevision={augustRevision}
        dragHandleProps={draggableProps?.dragHandleProps || undefined}
      />
      <DroppableEmptySection
        sectionId={sectionId}
        isHoveringForMoment={emptySectionIdHoveringOver === sectionId}
        onDragEnterMoment={(hovering: boolean) => {
          if (hovering) {
            setEmptySectionIdHoveringOver(sectionId);
          } else {
            setEmptySectionIdHoveringOver(null);
          }
        }}
        mustCreateNewSection={
          // when we have an empty default section, use this "empty space" to fill
          // the default section with items instead of creating a new section
          !(sectionId === DEFAULT_SECTION_ID && items.length === 0)
        }
      />
    </div>
  );
};
